'use client';

import customFetch from '@/lib/utils/fetch';
import { useAsyncEffect } from 'ahooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import Chat from './Chat';
import { useSnackbar } from '@/contexts/SnackbarContext';
import { useRightDrawer } from '@/contexts/DrawerContext';
import { useEventEmitter } from '@/hooks/useEventEmitter';
import { createUid } from '@/lib/utils/string';
import { useUserContextStore } from '@/stores/userContextStore';
import { useTranslation } from '@/lib/i18n';

function detectQueryLang(text: string): string {
  const ratio = (text.match(/[一-龥]/g) || []).length / text.length;
  return ratio > 0.1 ? 'zh' : 'en';
}

interface ChatWindowProps {
  chatId: string;
  query: string;
  history: any[];
  isShared?: boolean;
  floating?: boolean;
  queryType?: 'auto' | 'crypto' | 'predx';
  currentQuery?: string;
  onCurrentQueryConsumed?: () => void;
}
export default function ChatWindow(props: ChatWindowProps) {
  const {
    chatId,
    query,
    history,
    isShared = false,
    floating = false,
    queryType = 'auto',
    currentQuery,
    onCurrentQueryConsumed,
  } = props;

  const [messages, setMessages] = useState<any[]>(history);
  const [loading, setLoading] = useState(false);
  const { showError } = useSnackbar();
  const { t } = useTranslation();
  const { isOpen: isDrawerOpen } = useRightDrawer();
  const emitter = useEventEmitter();
  const sentInitialMessageRef = useRef(false);
  const consumedCurrentQueryRef = useRef<string | null>(null);

  const clearCurrentQueryFromUrl = useCallback(() => {
    if (typeof window === 'undefined') return;

    const url = new URL(window.location.href);
    if (!url.searchParams.has('query')) return;

    url.searchParams.delete('query');
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`,
    );
  }, []);

  const sendMessage = useCallback(
    async (msg: string, messageId: string) => {
      let added = true;
      let thinkStatus: any[] = [];
      let webSources: any[] = [];
      let audioSources: any[] = [];
      const queryLanguage = detectQueryLang(msg);

      const userContext = useUserContextStore.getState().getUserContext();

      const newUserMessage: any = {
        content: msg,
        messageId: createUid(),
        chatId: chatId!,
        role: 'user' as const,
        createdAt: new Date(),
        userContext: userContext,
      };
      // 添加到 Messages
      setMessages((prev) => [
        ...prev,
        newUserMessage,
        {
          content: '',
          messageId,
          chatId: chatId!,
          role: 'assistant',
          thinkStatus: thinkStatus,
          queryLanguage,
        },
      ]);
      setLoading(true);

      const messageHandler = (data: any) => {
        if (data.type === 'error') {
          showError(data.data);
          const errorMessage = {
            messageId: createUid(),
            chatId: chatId!,
            createdAt: new Date(),
            role: 'assistant',
            content: data.data, //: ${error.message || 'Unknown error'}`,
            isError: true,
            retryMessage: msg, // 存储原始的用户消息以便重试
          };

          setMessages((prev) => [
            ...prev.slice(0, added ? prev.length - 1 : undefined),
            errorMessage,
          ]);
          setLoading(false);
          return;
        }

        if (data.type === 'stream_end') {
          if (!added) {
            setMessages((prevMessages) => [
              ...prevMessages,
              {
                content: '',
                messageId,
                chatId: chatId!,
                role: 'assistant',
                thinkStatus: thinkStatus,
                queryLanguage,
                createdAt: data.createdAt,
              },
              ,
            ]);
            added = true;
          } else {
            setMessages((prev) =>
              prev.map((message) => {
                // 只更新 assistant 消息,避免错误地更新用户消息
                if (message.messageId === messageId) {
                  return {
                    ...message,
                    createdAt: data.createdAt,
                  };
                }

                return message;
              }),
            );
          }
        }

        if (data.type === 'final_answer') {
          if (!added) {
            setMessages((prevMessages) => [
              ...prevMessages,
              {
                content: data.data,
                messageId,
                chatId: chatId!,
                role: 'assistant',
                thinkStatus: thinkStatus,
                queryLanguage,
                createdAt: new Date(),
                riskStatement: t(
                  'Generated by AI. Not investment advice. Please assess risks independently.',
                ),
              },
            ]);
            added = true;
          } else {
            setMessages((prev) =>
              prev.map((message) => {
                // 只更新 assistant 消息,避免错误地更新用户消息
                if (message.messageId === messageId) {
                  return {
                    ...message,
                    content: message.content + data.data,
                    // riskStatement:
                    //   'Generated by AI. Not investment advice. Please assess risks independently.',
                  };
                }

                return message;
              }),
            );
          }
        }

        // guardrail 触发替换时，始终用审核通过的答案覆盖已流式渲染的内容
        if (data.type === 'replace_final_answer') {
          setMessages((prev) =>
            prev.map((message) => {
              if (message.messageId === messageId) {
                return {
                  ...message,
                  content: data.data || '',
                  riskStatement: t(
                    'Generated by AI. Not investment advice. Please assess risks independently.',
                  ),
                };
              }
              return message;
            }),
          );
        }
        if (data.type.startsWith('tool_') && data.data !== 'task') {
          // 检查是否已存在相同 action 的 think 状态
          const existingIndex = thinkStatus.findIndex((item: any) => item.task === data.data);

          const newThinkStatus = {
            task: data.data,
            status: data.type === 'tool_end' ? 'done' : 'doing',
            agent: data.agent,
            createdAt: new Date(),
            metadata: data.metadata,
            toolCallId: data.metadata?.tool_call_id,
            description: data.metadata?.description || '',
          };

          if (existingIndex >= 0) {
            // 更新现有的 think 状态
            thinkStatus[existingIndex] = {
              ...newThinkStatus,
              description: thinkStatus[existingIndex].description || newThinkStatus.description,
            };
          } else {
            // 添加新的 think 状态
            thinkStatus.push(newThinkStatus);
          }

          // 如果消息还没有创建，先创建消息
          if (!added) {
            const messageBody: any = {
              content: '',
              messageId,
              chatId: chatId!,
              role: 'assistant',
              thinkStatus: [...thinkStatus],
              queryLanguage,
              createdAt: new Date(),
            };
            setMessages((prevMessages) => [...prevMessages, messageBody]);
            added = true;
          } else {
            // 更新现有消息中的 thinkStatus
            setMessages((prev) =>
              prev.map((message) => {
                if (message.messageId === messageId) {
                  return { ...message, thinkStatus: [...thinkStatus] };
                }
                return message;
              }),
            );
          }
        }

        if (data.type.endsWith('_thinking')) {
          const existingIndex = thinkStatus.findIndex(
            (item: any) => item.task === 'thinking' && item.agent === data.agent,
          );
          const _thinkStatus = {
            task: 'thinking',
            status: 'doing',
            agent: data.agent,
            createdAt: new Date(),
            metadata: data.metadata,
            toolCallId: data.metadata?.tool_call_id,
            content: data.data || '',
          };

          if (existingIndex >= 0) {
            // console.log('Updating existing thinkStatus for task:', _thinkStatus);
            // 更新现有的 think 状态
            thinkStatus[existingIndex] = {
              ..._thinkStatus,
              content: thinkStatus[existingIndex].content + _thinkStatus.content,
            };
          } else {
            // 添加新的 think 状态
            thinkStatus.push(_thinkStatus);
          }
          setMessages((prev) =>
            prev.map((message) => {
              if (message.messageId === messageId) {
                return { ...message, thinkStatus: [...thinkStatus] };
              }
              return message;
            }),
          );
        }

        if (data.type === 'reference_data') {
          const newSources = Array.isArray(data.data) ? data.data : [data.data];
          webSources.push(...newSources);
          if (!added) {
            setMessages((prevMessages) => [
              ...prevMessages,
              {
                content: '',
                messageId,
                chatId: chatId!,
                role: 'assistant',
                thinkStatus: [...thinkStatus],
                webSources: [...webSources],
                queryLanguage,
                createdAt: data.createdAt,
              },
            ]);
            added = true;
          } else {
            setMessages((prev) =>
              prev.map((message) => {
                if (message.messageId === messageId) {
                  return { ...message, webSources: [...(message.webSources || []), ...newSources] };
                }
                return message;
              }),
            );
          }
        }

        if (data.type === 'answer_paragraph') {
          const newParagraphs = (Array.isArray(data.data) ? data.data : [data.data]).filter(
            (item: any) => item != null,
          );
          audioSources.push(...newParagraphs);
          setMessages((prev) =>
            prev.map((message) => {
              if (message.messageId === messageId) {
                return { ...message, audioSources: [...audioSources] };
              }
              return message;
            }),
          );
        }
      };

      const resp = await customFetch('/api/predic/chat/stream', {
        method: 'POST',
        body: JSON.stringify({
          content: msg,
          chatId,
          userContext: userContext,
          queryType,
          messageId,
        }),
      });
      if (userContext) {
        useUserContextStore.getState().clearUserContext();
      }
      if (!resp?.body) throw new Error('No response body');
      const reader = resp.body?.getReader();
      const decoder = new TextDecoder();

      let partialChunk = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          setLoading(false);
          break;
        }

        partialChunk += decoder.decode(value, { stream: true });
        const lines = partialChunk.split('\n');
        partialChunk = lines.pop() || ''; // 保留不完整的行

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const dataStr = line.slice(6);
            if (!dataStr.trim()) continue;

            try {
              const data = JSON.parse(dataStr);
              messageHandler(data);
            } catch (e) {
              console.error('解析 SSE 数据失败:', e, dataStr);
            }
          }
        }
      }
    },
    [chatId],
  );

  useAsyncEffect(async () => {
    const normalizedCurrentQuery = currentQuery?.trim();
    if (normalizedCurrentQuery && consumedCurrentQueryRef.current !== normalizedCurrentQuery) {
      consumedCurrentQueryRef.current = normalizedCurrentQuery;
      onCurrentQueryConsumed?.();
      clearCurrentQueryFromUrl();

      sendMessage(normalizedCurrentQuery, createUid());
      return;
    }

    if (history.length === 0 && !isShared && !sentInitialMessageRef.current) {
      sentInitialMessageRef.current = true;
      sendMessage(query, createUid());
    }
  }, [
    sendMessage,
    query,
    history.length,
    isShared,
    currentQuery,
    onCurrentQueryConsumed,
    clearCurrentQueryFromUrl,
  ]);

  useEffect(() => {
    const onAction = (message: string) => {
      sendMessage(message, createUid());
    };
    emitter.on('air-sendmessage', onAction);
    return () => {
      emitter.off('air-sendmessage', onAction);
    };
  }, [emitter, sendMessage]);

  return (
    <div className="max-w-190 mx-auto">
      <Chat
        chatId={chatId}
        messages={messages}
        sendMessage={(message: string) => {
          sendMessage(message, createUid());
        }}
        loading={loading}
        isDrawerOpen={isDrawerOpen}
        isShared={isShared}
        floating={floating}
      />
    </div>
  );
}
